'use client';

import { useMemo, useCallback } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import type { BulkPayload } from '@/types/bulk-action';
import { getSortingStateParser } from '@/lib/parsers';
import { Users, UserPlus, Pencil, Plus, X, Eye, Upload } from 'lucide-react';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import { useSelectAllPages } from '@/hooks/use-select-all-pages';
import { useRelativeTime } from '@/hooks/use-relative-time';
import { useHashDialog, useHashParamDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import { Link } from '@/i18n/routing';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SelectAllBanner } from '@/components/select-all-banner';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import { PermissionGate } from '@/features/auth';
import { labQueries } from '@/features/labs';
import { RoleColorBadge, roleKeys, rolesApi } from '@/features/roles';
import type { Role } from '@/features/roles';
import { userQueries } from '../services/users.queries';
import {
  useDeactivateUserMutation,
  useActivateUserMutation,
  useUpdateDriverAvailabilityMutation,
  useUpdateOperatorAvailabilityMutation
} from '../services/users.mutations';
import { AssignRoleToUserDialog } from './assign-role-to-user-dialog';
import { RemoveRoleFromUserDialog } from './remove-role-from-user-dialog';
import { isSuperAdminUser } from '../utils';
import { UsersImportDialog } from './users-import-dialog';
import { UsersTableActionBar } from './users-table-action-bar';
import { USERS_HASH } from '../constants';
import { UserCard } from './user-card';
import { UserCompactRow } from './user-compact-row';
import type {
  User,
  UsersListParams,
  DriverAvailability,
  OperatorAvailability
} from '../types';

function getRoleDisplayName(
  role: { name: string; name_en: string | null; name_ar: string | null },
  locale: string
): string {
  if (locale === 'ar' && role.name_ar) return role.name_ar;
  if (locale === 'en' && role.name_en) return role.name_en;
  return role.name_en || role.name_ar || role.name;
}

export function UserListing() {
  const t = useTranslations('users');
  const locale = useLocale();
  // Overlay open-state lives in the URL hash (#import, #assign-role/<userId>,
  // #remove-role/<userId>/<roleName>) so overlays are deep-linkable and close on
  // browser Back. Destructure the stable callbacks so they can go in hook dep
  // arrays without tripping the React Compiler's manual-memoization check.
  const { open: importOpen, onOpenChange: toggleImport } = useHashDialog(
    USERS_HASH.import
  );
  const {
    open: assignOpen,
    param: assignId,
    openWith: openAssign,
    onOpenChange: closeAssign
  } = useHashParamDialog(USERS_HASH.assignRole);
  const {
    open: removeOpen,
    params: removeParams,
    openWith: openRemove,
    onOpenChange: closeRemove
  } = useHashParamDialog(USERS_HASH.removeRole);
  const [view, setView] = useDataView('users');
  const relativeTime = useRelativeTime('users');

  // Lab offices for filter
  const { data: labData } = useQuery(labQueries.all());
  const labSettings = (labData as any)?.data ?? labData ?? {};
  const officeFilterOptions = useMemo(() => {
    const rawOffices: unknown[] = Array.isArray(labSettings.offices)
      ? labSettings.offices
      : [];
    return rawOffices
      .map((item) => {
        if (typeof item === 'string')
          return { ar: item, en: '', status: 'active' };
        return item as { ar: string; en: string; status: string };
      })
      .filter((item) => item.status !== 'inactive')
      .map((item) => ({ value: item.ar, label: item.ar || item.en }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(labSettings.offices)]);

  // Roles infinite select for filter
  const roleInfinite = useInfiniteSelect<Role>({
    queryKey: roleKeys.lists(),
    queryFn: (params) => rolesApi.getAll(params),
    mapOption: (r) => ({ value: r.name, label: getRoleDisplayName(r, locale) })
  });

  // Toggle status mutations
  const deactivateMutation = useDeactivateUserMutation();
  const activateMutation = useActivateUserMutation();
  const driverAvailabilityMutation = useUpdateDriverAvailabilityMutation();
  const operatorAvailabilityMutation = useUpdateOperatorAvailabilityMutation();

  const handleToggleStatus = useCallback(
    async (user: User) => {
      if (isSuperAdminUser(user) || user.status === 'locked') return;
      try {
        if (user.status === 'active') {
          await deactivateMutation.mutateAsync(user.id);
        } else {
          await activateMutation.mutateAsync(user.id);
        }
      } catch {
        // handled by global MutationCache
      }
    },
    [deactivateMutation, activateMutation]
  );

  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('name')} />
        ),
        cell: ({ row, column }) => (
          <span className='font-medium'>
            <HighlightMatch
              text={row.original.name}
              query={getColumnSearchTerms(column)}
            />
          </span>
        ),
        enableColumnFilter: true,
        meta: {
          label: t('searchPlaceholder'),
          variant: 'text' as const,
          placeholder: t('searchPlaceholder'),
          permission: 'users.column.name'
        }
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('email')} />
        ),
        cell: ({ row }) => row.original.email,
        enableSorting: false,
        meta: { label: t('email'), permission: 'users.column.email' }
      },
      {
        id: 'office',
        accessorKey: 'office',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('office')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground text-sm'>
            {row.original.office ?? '—'}
          </span>
        ),
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('filterOffice'),
          variant: 'multiSelect' as const,
          options: officeFilterOptions,
          permission: 'users.column.office'
        }
      },
      {
        id: 'workplace',
        accessorKey: 'workplace',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('workplace')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground text-sm'>
            {row.original.workplace ?? '—'}
          </span>
        ),
        enableSorting: false,
        meta: { label: t('workplace'), permission: 'users.column.workplace' }
      },
      {
        id: 'roles',
        accessorFn: (row) => row.roles.map((r) => r.name).join(', '),
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('roles')} />
        ),
        cell: ({ row }) => {
          const user = row.original;
          const isSuperAdmin = isSuperAdminUser(user);
          return (
            <div className='flex flex-wrap gap-1'>
              {user.roles.length > 0 ? (
                user.roles.map((role) =>
                  isSuperAdmin ? (
                    <RoleColorBadge
                      key={role.name}
                      name={getRoleDisplayName(role, locale)}
                      color={role.color}
                    />
                  ) : (
                    <PermissionGate
                      key={role.name}
                      permissions={['roles.edit']}
                      fallback={
                        <RoleColorBadge
                          name={getRoleDisplayName(role, locale)}
                          color={role.color}
                        />
                      }
                    >
                      <span
                        className='inline-flex items-center rounded-full py-0.5 ps-2.5 pe-1 text-xs font-medium'
                        style={{
                          backgroundColor: `${role.color}20`,
                          color: role.color,
                          border: `1px solid ${role.color}40`
                        }}
                      >
                        <span
                          className='me-1.5 h-2 w-2 rounded-full'
                          style={{ backgroundColor: role.color }}
                        />
                        {getRoleDisplayName(role, locale)}
                        <button
                          type='button'
                          onClick={() => openRemove(String(user.id), role.name)}
                          className='ms-1 inline-flex h-4 w-4 items-center justify-center rounded-full transition-colors hover:bg-black/10'
                          style={{ color: role.color }}
                        >
                          <X className='h-3 w-3' />
                        </button>
                      </span>
                    </PermissionGate>
                  )
                )
              ) : (
                <span className='text-muted-foreground text-sm'>
                  {t('noRolesAssigned')}
                </span>
              )}
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('filterRole'),
          variant: 'infiniteMultiSelect' as const,
          permission: 'users.column.roles'
        }
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('status')} />
        ),
        cell: ({ row }) => {
          const user = row.original;
          const isDisabled =
            isSuperAdminUser(user) ||
            user.status === 'locked' ||
            deactivateMutation.isPending ||
            activateMutation.isPending;
          return (
            <Switch
              checked={user.status === 'active'}
              disabled={isDisabled}
              onCheckedChange={() => handleToggleStatus(user)}
            />
          );
        },
        enableColumnFilter: true,
        meta: {
          label: t('filterStatus'),
          variant: 'multiSelect' as const,
          options: [
            { label: t('statusActive'), value: 'active' },
            { label: t('statusLocked'), value: 'locked' },
            { label: t('statusInactive'), value: 'inactive' }
          ],
          permission: 'users.column.status'
        }
      },
      {
        id: 'availability',
        header: () => t('availability'),
        cell: ({ row }) => {
          const user = row.original;
          const isDriver = user.roles.some((r) => r.name === 'Driver');
          const isOperator = user.roles.some((r) => r.name === 'Operator');

          const profile = isDriver
            ? user.driver_profile
            : isOperator
              ? user.operator_profile
              : null;

          if (!profile) return null;

          return (
            <Select
              value={profile.status}
              onValueChange={(v) => {
                if (isDriver) {
                  driverAvailabilityMutation.mutate({
                    userId: user.id,
                    status: v as DriverAvailability
                  });
                } else {
                  operatorAvailabilityMutation.mutate({
                    userId: user.id,
                    status: v as OperatorAvailability
                  });
                }
              }}
              disabled={
                driverAvailabilityMutation.isPending ||
                operatorAvailabilityMutation.isPending
              }
            >
              <SelectTrigger className='h-8 w-auto min-w-[130px]'>
                <div className='flex items-center gap-1.5'>
                  <span
                    className={cn(
                      'h-2 w-2 rounded-full',
                      profile.status === 'available' && 'bg-green-500',
                      profile.status === 'busy' && 'bg-red-500',
                      profile.status === 'on_break' && 'bg-yellow-500'
                    )}
                  />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='available'>
                  {t('availabilityAvailable')}
                </SelectItem>
                <SelectItem value='busy'>{t('availabilityBusy')}</SelectItem>
                <SelectItem value='on_break'>
                  {t('availabilityOnBreak')}
                </SelectItem>
              </SelectContent>
            </Select>
          );
        },
        enableSorting: false,
        meta: { label: t('availability') }
      },
      {
        id: 'lastLoginAt',
        accessorKey: 'last_login_at',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('lastLoginAt')} />
        ),
        cell: ({ row }) => {
          const value = row.original.last_login_at;
          if (!value) {
            return (
              <span className='text-muted-foreground text-sm'>
                {t('neverLoggedIn')}
              </span>
            );
          }
          return <span className='text-sm'>{relativeTime(value)}</span>;
        },
        meta: {
          label: t('lastLoginAt'),
          permission: 'users.column.lastLoginAt'
        }
      },
      {
        id: 'actions',
        header: () => <div className='text-end'>{t('actions')}</div>,
        cell: ({ row }) => {
          const user = row.original;
          return (
            <div className='flex items-center justify-end gap-2'>
              {(user.driver_profile || user.operator_profile) && (
                <PermissionGate permissions={['users.edit']}>
                  <Button variant='ghost' size='icon' asChild>
                    <Link href={`/dashboard/users/${user.id}/profile`}>
                      <Eye className='h-4 w-4' />
                    </Link>
                  </Button>
                </PermissionGate>
              )}
              {!isSuperAdminUser(user) && (
                <PermissionGate permissions={['roles.edit']}>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={() => openAssign(String(user.id))}
                  >
                    <UserPlus className='h-4 w-4' />
                  </Button>
                </PermissionGate>
              )}
              <PermissionGate permissions={['users.edit']}>
                <Button variant='ghost' size='icon' asChild>
                  <Link href={`/dashboard/users/${user.id}/edit`}>
                    <Pencil className='h-4 w-4' />
                  </Link>
                </Button>
              </PermissionGate>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, locale, officeFilterOptions, openAssign, openRemove]
  );

  // Read URL state for query params (same keys useDataTable uses internally)
  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState('name', parseAsString.withDefault(''));
  const [urlStatus] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlOffice] = useQueryState(
    'office',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlRoles] = useQueryState(
    'roles',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<User>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<User>[])
  );

  const queryParams = useMemo<UsersListParams>(() => {
    const params: UsersListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch) params.search = urlSearch;
    if (urlStatus.length) params.status = urlStatus;
    if (urlOffice.length) params.office = urlOffice;
    if (urlRoles.length) params.role = urlRoles;
    params.sort_by = urlSorting[0]?.id ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';
    return params;
  }, [
    urlPage,
    urlPerPage,
    urlSearch,
    urlStatus,
    urlOffice,
    urlRoles,
    urlSorting
  ]);

  const { data, isPending, isPlaceholderData } = useQuery(
    userQueries.list(queryParams)
  );

  const users = useMemo(() => (data?.data ?? []) as User[], [data]);
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  // Resolve the hash id param back to the loaded row (row must be on the
  // current page; deep-linking an off-page id simply won't open the overlay).
  const assignTarget = assignOpen
    ? (users.find((u) => String(u.id) === assignId) ?? null)
    : null;
  const removeUser = removeOpen
    ? (users.find((u) => String(u.id) === removeParams[0]) ?? null)
    : null;
  const removeRoleName = removeOpen ? (removeParams[1] ?? null) : null;

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<User>({
    columns: filteredColumns,
    pageKey: 'users',
    data: users,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const selectAll = useSelectAllPages(table, pagination?.total ?? 0);

  const getBulkPayload = useCallback((): BulkPayload => {
    if (selectAll.isAllPagesSelected) {
      const { page, per_page, sort_by, sort_direction, ...filters } =
        queryParams;
      return { mode: 'all', filters };
    }
    const selectedIds = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    return { mode: 'ids', ids: selectedIds };
  }, [selectAll.isAllPagesSelected, queryParams, table]);

  const handleBulkComplete = useCallback(() => {
    selectAll.reset();
  }, [selectAll]);

  const handleAssignRole = useCallback(
    (user: User) => openAssign(String(user.id)),
    [openAssign]
  );

  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('name'), value: 'name' },
      { label: t('email'), value: 'email' },
      { label: t('status'), value: 'status' },
      { label: t('lastLoginAt'), value: 'last_login_at' },
      { label: t('createdAt'), value: 'created_at' }
    ],
    [t]
  );

  return (
    <div className='space-y-6'>
      {/* Toolbar: View Toggle + Add */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='users'
          />
        </div>
        <div className='flex items-center gap-4'>
          <PermissionGate permissions={['users.create']}>
            <Button
              variant='outline'
              size='sm'
              onClick={() => toggleImport(true)}
            >
              <Upload className='me-2 h-4 w-4' />
              {t('importFromExcel')}
            </Button>
          </PermissionGate>
          <PermissionGate permissions={['roles.create']}>
            <Button asChild>
              <Link href='/dashboard/users/new'>
                <Plus className='me-2 h-4 w-4' />
                {t('addAdmin')}
              </Link>
            </Button>
          </PermissionGate>
        </div>
      </div>

      <DataTableToolbar
        table={table}
        infiniteConfigs={{ roles: roleInfinite }}
        viewFilterPermission='users.view_filter'
      />

      {/* Data */}
      {isPending ? (
        <ListingSkeleton view={view} />
      ) : users.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <Users className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noUsers')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {users.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                onAssignRole={handleAssignRole}
                onToggleStatus={handleToggleStatus}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {users.map((user) => (
              <UserCompactRow
                key={user.id}
                user={user}
                onAssignRole={handleAssignRole}
                onToggleStatus={handleToggleStatus}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col gap-2',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <SelectAllBanner
            isAllPagesSelected={selectAll.isAllPagesSelected}
            isCurrentPageFullySelected={selectAll.isCurrentPageFullySelected}
            currentPageCount={users.length}
            totalCount={pagination?.total ?? 0}
            pageSize={tableState.pagination.pageSize}
            onSelectAllPages={() => selectAll.setAllPagesSelected(true)}
            onClearSelection={() => selectAll.reset()}
          />
          <DataTable
            table={table}
            actionBar={
              <UsersTableActionBar
                table={table}
                getBulkPayload={getBulkPayload}
                onComplete={handleBulkComplete}
                isAllPagesSelected={selectAll.isAllPagesSelected}
                totalCount={pagination?.total ?? 0}
              />
            }
          ></DataTable>
        </div>
      )}

      {/* Assign role dialog — #assign-role/<userId> */}
      <AssignRoleToUserDialog
        open={assignOpen && !!assignTarget}
        onOpenChange={closeAssign}
        user={assignTarget}
      />

      {/* Remove role dialog — #remove-role/<userId>/<roleName> */}
      <RemoveRoleFromUserDialog
        open={removeOpen && !!removeUser && !!removeRoleName}
        onOpenChange={closeRemove}
        user={removeUser}
        roleName={removeRoleName}
      />

      {/* Import dialog — #import */}
      <UsersImportDialog open={importOpen} onOpenChange={toggleImport} />
    </div>
  );
}
